import { List, Map } from 'immutable';
import { applyMiddleware, compose, createStore } from 'redux';

import { assertModuleSanity } from './assertModuleSanity';
import { combineReducersMap } from './composeReducer';
import { composeAppCoreModule } from './core';
import { IModule, ISomeEffect } from './types';
import { IAnyActionComposerMap } from './composeActions';

/**
 * Middleware to enable the redux devtools chrome extension.
 * https://github.com/zalmoxisus/redux-devtools-extension
 */

const NODE_ENV = globalThis?.process?.env?.NODE_ENV;
const isBrowser = typeof window !== undefined;

const reduxDevtools =
  NODE_ENV &&
  isBrowser &&
  (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
  (window as any).__REDUX_DEVTOOLS_EXTENSION__();

function combineModuleReducers<P, T, S extends IAnyActionComposerMap<T>>(modules: IModule<P, T, S>[]) {
  const moduleReducerMap = modules
    .filter((mod) => mod.state)
    .map((mod) => mod.state)
    .reduce((list, state) => list.concat(state)) // flatten via reduce because immutable flatten loses type information
    .reduce((reducers, { branchKey, reducer }) => Object.assign(reducers, { [branchKey]: reducer }), {});

  return combineReducersMap(moduleReducerMap, combineModuleInitialStates(modules));
}

function selectModuleEffects<P, T, S extends IAnyActionComposerMap<T>>(modules: IModule<P, T, S>[]) {
  return modules
    .filter((mod) => mod.effects)
    .map((mod) => mod.effects)
    .reduce((list, effectGroup) => list.concat(effectGroup), List<{ [key: string]: ISomeEffect }>())
    .map((effectGroup) => Map(effectGroup).valueSeq().toList())
    .reduce((list, effects) => list.concat(effects), List<ISomeEffect>())
    .reduce((allEffects, effect) => allEffects.set(effect.effectType, effect), Map<string, ISomeEffect>());
}

export function combineModuleInitialStates<
  P,
  T,
  S extends IAnyActionComposerMap<T>,
  PM extends { state?: IModule<P, T, S>['state'] }
>(modules: PM[]) {
  return modules
    .filter((mod) => mod.state)
    .map((mod) => mod.state)
    .reduce((list, state) => list.concat(state)) // flatten via reduce because immutable flatten loses type information
    .reduce((initialState, mod) => initialState.set(mod.branchKey, mod.initialState), Map<string, any>());
}

function selectModuleMiddleware<P, T, S extends IAnyActionComposerMap<T>>(modules: IModule<P, T, S>[]) {
  return modules.map((mod) => mod.middleware).reduce((list, state) => list.concat(state));
}

function selectModuleStoreEnhancers<P, T, S extends IAnyActionComposerMap<T>>(modules: IModule<P, T, S>[]) {
  return modules.map((mod) => mod.storeEnhancers).reduce((list, state) => list.concat(state));
}

function selectModuleSubscribers<P, T, S extends IAnyActionComposerMap<T>>(modules: IModule<P, T, S>[]) {
  return modules.map((mod) => mod.subscribers).reduce((list, state) => list.concat(state));
}

function composeMiddlewareAndEnhancers<P, T, S extends IAnyActionComposerMap<T>>(modules: IModule<P, T, S>[]) {
  if (reduxDevtools) {
    return (compose as any)(
      applyMiddleware(...selectModuleMiddleware(modules)),
      ...selectModuleStoreEnhancers(modules),
      reduxDevtools
    );
  }

  return (compose as any)(applyMiddleware(...selectModuleMiddleware(modules)), ...selectModuleStoreEnhancers(modules));
}

export function createStoreWithModules<P, T, S extends IAnyActionComposerMap<T>>(modulesToLoad: IModule<P, T, S>[]) {
  const userModules: IModule<P, T, S>[] = modulesToLoad
    .reduce((modsAndDeps, mod) => modsAndDeps.concat(mod.dependencies).push(mod), List<IModule<P, T, S>>())
    .toSet()
    .groupBy((m) => m.name)
    .reduce((moduleList, moduleGroup) => {
      if (!moduleGroup.first().combine) {
        return moduleList.push(moduleGroup.first());
      }

      return moduleList.push(
        moduleGroup.first().combine(moduleGroup.first(), moduleGroup.map((mod) => mod.params).toList())
      );
    }, List<IModule<P, T, S>>())
    .toArray();

  const modules = [composeAppCoreModule(selectModuleEffects(userModules)), ...userModules];

  if (process.env.NODE_ENV === 'test') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { composeAppTestModule } = require('./testModule');
    modules.push(composeAppTestModule);
  }

  assertModuleSanity(modules as unknown as any);

  const store = createStore(
    combineModuleReducers(modules as unknown as any),
    combineModuleInitialStates(modules),
    composeMiddlewareAndEnhancers(modules as unknown as any)
  );

  selectModuleSubscribers(modules as unknown as any).forEach((subscriber) =>
    store.subscribe(() => subscriber.onChange(subscriber.selector(store.getState())))
  );

  return store;
}
